import LegalLayout, { LegalSection } from "./LegalLayout";
import { SITE } from "@/config/site";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="December 2025" testId="privacy-page">
      <p className="text-lg font-light leading-relaxed text-[#15243D]">
        Anchor is a small, one-person coaching practice. We collect only what
        is necessary to respond to you, deliver what you’ve asked for, and
        keep our work together legally and ethically clean. This page explains
        what that means in plain language.
      </p>

      <LegalSection n="01" title="What we collect">
        <p>When you interact with this site, we may collect:</p>
        <ul className="space-y-2 pl-1">
          {[
            "Name and email — when you sign up for the 28-day program, submit the contact form, apply for coaching, or sign the agreement.",
            "Phone number, written responses, and commitment selections — when you apply for coaching.",
            "Signature, signing date, and agreement choices — when you sign the coaching agreement.",
            "Payment details — handled directly by Stripe; we never see or store your card number.",
          ].map((i) => (
            <li key={i} className="flex items-baseline gap-3">
              <span className="text-[#C07865] text-xs">—</span>
              {i}
            </li>
          ))}
        </ul>
      </LegalSection>

      <LegalSection n="02" title="How we use it">
        <p>Information you submit is used to:</p>
        <ul className="space-y-2 pl-1">
          {[
            "Reply to you personally.",
            "Deliver the 28-day program and any digital materials you’ve purchased.",
            "Onboard you into 1:1 coaching if we move forward together.",
            "Send transactional emails (welcome notes, application acknowledgements, signed agreement copies).",
          ].map((i) => (
            <li key={i} className="flex items-baseline gap-3">
              <span className="text-[#C07865] text-xs">—</span>
              {i}
            </li>
          ))}
        </ul>
        <p>
          We do not sell your information. We do not share it with advertisers.
          We do not use it for any purpose unrelated to our work together.
        </p>
      </LegalSection>

      <LegalSection n="03" title="Third-party services we use">
        <ul className="space-y-2 pl-1">
          {[
            ["Resend", "Sending transactional emails on our behalf."],
            ["Stripe", "Processing payments. Stripe handles all card details and complies with PCI-DSS."],
            ["Calendly", "Scheduling discovery calls. Calendly receives the name, email, and any notes you provide when booking."],
            ["MongoDB Atlas", "Securely storing form submissions in encrypted databases."],
          ].map(([n, d]) => (
            <li key={n} className="flex items-baseline gap-3">
              <span className="text-[#C07865] text-xs">—</span>
              <span><span className="text-[#15243D]">{n}</span> — {d}</span>
            </li>
          ))}
        </ul>
        <p>
          Each service has its own privacy policy governing how they handle the
          data we pass to them. We pass only what is strictly necessary.
        </p>
      </LegalSection>

      <LegalSection n="04" title="Confidentiality of coaching conversations">
        <p>
          Anything you share inside the 1:1 coaching relationship — verbally
          or in writing — remains strictly confidential. The only exceptions
          are situations required by law (e.g. credible risk of serious harm
          to yourself or another person).
        </p>
      </LegalSection>

      <LegalSection n="05" title="Cookies and tracking">
        <p>
          We do not use third-party advertising trackers or analytics that
          follow you across the web. The site uses minimal local storage to
          remember your admin login (if applicable) — nothing else.
        </p>
      </LegalSection>

      <LegalSection n="06" title="Your rights">
        <p>
          You can request a copy of the data we hold about you, ask for it to
          be corrected, or ask for it to be deleted at any time. Email{" "}
          <a
            href={`mailto:${SITE.contactEmail}`}
            className="text-[#C07865] hover:text-[#A66352] underline-offset-4"
          >
            {SITE.contactEmail}
          </a>{" "}
          and we’ll respond within a few days.
        </p>
      </LegalSection>

      <LegalSection n="07" title="Children">
        <p>
          Anchor is intended for adults. We do not knowingly collect
          information from anyone under 18.
        </p>
      </LegalSection>

      <LegalSection n="08" title="Changes to this policy">
        <p>
          If this policy changes meaningfully, we’ll update the “last updated”
          date above and, for material changes affecting active coaching
          clients, notify you by email.
        </p>
      </LegalSection>

      <LegalSection n="09" title="Contact">
        <p>
          Questions about your privacy, your data, or anything else legal —
          write to{" "}
          <a
            href={`mailto:${SITE.contactEmail}`}
            className="text-[#C07865] hover:text-[#A66352] underline-offset-4"
          >
            {SITE.contactEmail}
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
